import type {ReactElement} from 'react'
import _ from 'lodash'
// Layouts
import Section from './section'
// Components
import GroupListBlock from './group-list-block'
import ImageBlock from './image-block'
import SponsorBlock from './sponsor-block'
import StartButtonBlock from './start-button-block'
import TextBlock from './text-block'
import TitleBlock from './title-block'
import QuestionListBlock from './question-list-block'
import RenderedTextBlock from './rendered-text-block'
// Types
import type {Ballot, BallotPageBlock} from '../../types/ballot'

interface BlocksRendererProps {
  ballot: Ballot
  blocks: BallotPageBlock[] | null
}

const BlocksRenderer = ({
  ballot,
  blocks,
}: BlocksRendererProps): ReactElement | null => {
  const renderBlock = (block: BallotPageBlock, blockIndex: number): ReactElement | null => {
    if (_.isEmpty(block)) return null

    const blockType = block.blockType
    if (!blockType) return null

    switch (blockType) {
    case 'groupListBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
          styleModifiers={['--is-making-room-for-footer-ad']}
        >
          <GroupListBlock
            ballot={ballot}
            block={block}
          />
        </Section>
      )
    case 'imageBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
          styleModifiers={[
            '--is-display-block',
            '--is-centered-horizontally',
            '--children-are-centered-horizontally',
          ]}
        >
          <ImageBlock
            block={block}
          />
        </Section>
      )
    case 'questionListBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
        >
          <QuestionListBlock
            block={block}
          />
        </Section>
      )
    case 'renderedTextBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
          styleModifiers={[
            '--is-display-block',
            '--is-centered-horizontally',
            '--children-are-centered-horizontally',
          ]}
        >
          <RenderedTextBlock
            block={block}
          />
        </Section>
      )
    case 'sponsorBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
        >
          <SponsorBlock
            ballot={ballot}
          />
        </Section>
      )
    case 'startButtonBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
          styleModifiers={[
            '--is-flex-column',
            '--is-centered-horizontally',
          ]}
        >
          <StartButtonBlock
            ballot={ballot}
            block={block}
          />
        </Section>
      )
    case 'textBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
        >
          <TextBlock
            block={block}
          />
        </Section>
      )
    case 'titleBlock':
      return (
        <Section
          key={`block-${blockIndex}`}
          marginTopOverride={block?.formValuesObject?.blockSpaceAbove}
          marginBottomOverride={block?.formValuesObject?.blockSpaceBelow}
        >
          <TitleBlock
            block={block}
          />
        </Section>
      )
    default:
      return null
    }
  }

  if (_.isEmpty(blocks)) return null

  return <>
    {_.map(blocks, (currentBlock, index) => {
      return (
        renderBlock(currentBlock, index)
      )
    })}
  </>
}

export default BlocksRenderer
